import { useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SERVICES = [
  {
    num: '01',
    title: '1:1 Coaching',
    sub: 'A private journey home',
    text: 'Rooted in ontological coaching, reflective practice, mindfulness, and evidence-informed wellbeing approaches, Presence Coaching explores the stories, patterns, and perspectives that shape how women see themselves and the choices they make. Through greater awareness and reflection, new possibilities can emerge.',
    image: '/images/12.webp',
    bg: '#F5F0E8',
    dark: false,
  },
  {
    num: '02',
    title: 'Group Circles',
    sub: 'Healing happens in connection',
    text: 'Small, intimate circles of women walking the same quiet path. Eight weeks of shared presence, honest conversation, and the profound relief of being truly seen — perhaps for the first time in years.',
    image: '/images/11.webp',
    bg: '#EDE6D8',
    dark: false,
  },
  {
    num: '03',
    title: 'Retreats & Workshops',
    sub: 'Step out of your life, into yourself',
    text: 'Immersive day retreats in nature. Breathwork, stillness, guided reflection, and long unhurried walks. You arrive carrying everything — you leave having set some of it down.',
    image: '/images/9.webp',
    bg: '#1A4D38',
    dark: true,
  },
];

const Services = () => {
  const root = useRef(null);
  const track = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panels = gsap.utils.toArray('.service-panel');
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: () => `+=${(panels.length - 1) * 100}%`,
          pin: true,
          scrub: 1.3,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
      tl.to(track.current, { x: () => -(track.current.scrollWidth - window.innerWidth), ease: 'none' }, 0);
      tl.to('.services-progress', { scaleX: 1, ease: 'none' }, 0);
      panels.forEach((panel) => {
        const img = panel.querySelector('.service-img img');
        gsap.fromTo(img, { scale: 1.2 }, {
          scale: 1, ease: 'none',
          scrollTrigger: {
            trigger: root.current,
            start: 'top top',
            end: () => `+=${(panels.length - 1) * 100}%`,
            scrub: 1.5,
            invalidateOnRefresh: true,
          },
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section id="services" ref={root} data-testid="services-section" className="relative overflow-hidden bg-[#F5F0E8]">
      <div ref={track} className="flex h-[100svh] w-max will-change-transform">
        {SERVICES.map((s) => (
          <div
            key={s.num}
            data-testid={`service-panel-${s.num}`}
            className="service-panel w-screen h-full flex-shrink-0 flex items-center"
            style={{ backgroundColor: s.bg }}
          >
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 px-6 md:px-20 items-center pt-16 pb-14 md:py-0">
              <div className={`max-w-lg ${s.dark ? 'text-[#F5F0E8]' : 'text-[#2C2C2A]'}`}>
                <p className="font-serif-display italic text-5xl md:text-8xl font-light mb-4 md:mb-8 text-[#7A9E86]">{s.num}</p>
                <p className={`text-[10px] md:text-[11px] uppercase tracking-[0.35em] md:tracking-[0.4em] mb-3 md:mb-5 ${s.dark ? 'text-[#C98B3A]' : 'text-[#B85C38]'}`}>{s.sub}</p>
                <h3 className="font-serif-display font-light text-3xl sm:text-4xl md:text-6xl leading-[1.08] mb-4 md:mb-8">{s.title}</h3>
                <p className={`text-sm md:text-lg leading-[1.8] md:leading-[1.9] font-light ${s.dark ? 'text-[#F5F0E8]/70' : 'text-[#2C2C2A]/70'}`}>{s.text}</p>
              </div>
              <div className="flex justify-center md:justify-end">
                <div className="service-img img-zoom w-full max-w-xs md:max-w-md aspect-[16/10] md:aspect-[3/4] overflow-hidden">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover will-change-transform" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="absolute bottom-6 md:bottom-12 left-6 md:left-20 right-6 md:right-20 h-px bg-[#2C2C2A]/15">
        <div className="services-progress h-full bg-[#B85C38] origin-left" style={{ transform: 'scaleX(0)' }} />
      </div>
    </section>
  );
};

export default Services;
